import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  Key, 
  Copy, 
  Plus, 
  Trash2,
  CheckCircle,
  XCircle,
  Download
} from "lucide-react";
import { useEnrollmentTokens } from "@/hooks/useEnrollmentTokens";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDistanceToNow } from "date-fns";

export function EnrollmentTokenManager() {
  const { tokens, loading, generateToken, fetchTokens, revokeToken } = useEnrollmentTokens();
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState("");

  useEffect(() => {
    fetchTokens();
  }, []);

  const handleGenerateToken = async () => {
    const token = await generateToken(deviceName || undefined);
    if (token) {
      setNewToken(token);
      setShowTokenDialog(true);
      setDeviceName("");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Token copied to clipboard",
    });
  };

  const windowsInstallCommand = newToken
    ? `PowerShell -ExecutionPolicy Bypass -File "$env:USERPROFILE\\Downloads\\windows-metrics-collector.ps1" -Setup -EnrollmentToken "${newToken}"`
    : "";

  const downloadScript = () => {
    const scriptUrl = '/windows-metrics-collector.ps1';
    const link = document.createElement('a');
    link.href = scriptUrl;
    link.download = 'windows-metrics-collector.ps1';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download started",
      description: "PowerShell agent script is downloading",
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Agent Installation
          </CardTitle>
          <CardDescription>
            Generate enrollment tokens to register new monitoring agents
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label htmlFor="deviceName">Device Name (Optional)</Label>
                <Input
                  id="deviceName"
                  placeholder="e.g., Production-Server-01"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                />
              </div>
              <Button onClick={handleGenerateToken} disabled={loading}>
                <Plus className="mr-2 h-4 w-4" />
                Generate Token
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadScript} className="flex-1">
                <Download className="mr-2 h-4 w-4" />
                Download Windows Agent
              </Button>
              <Button variant="outline" className="flex-1" disabled>
                <Download className="mr-2 h-4 w-4" />
                Download Linux Agent
              </Button>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-3">Active Tokens</h4>
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No enrollment tokens yet. Generate one to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    tokens.map((token) => (
                      <TableRow key={token.id}>
                        <TableCell className="font-medium">
                          {token.device_name || 'Unnamed Device'}
                        </TableCell>
                        <TableCell>
                          {token.is_active && new Date(token.expires_at) > new Date() ? (
                            <Badge variant="default" className="flex items-center gap-1 w-fit">
                              <CheckCircle className="h-3 w-3" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                              <XCircle className="h-3 w-3" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {token.uses_count} / {token.max_uses}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(token.expires_at) < new Date() 
                            ? 'Expired' 
                            : formatDistanceToNow(new Date(token.expires_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDistanceToNow(new Date(token.created_at), { addSuffix: true })}
                        </TableCell>
                        <TableCell className="text-right">
                          {token.is_active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => revokeToken(token.id)}
                              disabled={loading}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showTokenDialog} onOpenChange={setShowTokenDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Enrollment Token Generated</DialogTitle>
            <DialogDescription>
              Copy this token now - it won't be shown again. Use it to register your monitoring agent.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Enrollment Token</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={newToken || ''}
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(newToken || '')}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Installation Instructions</Label>
              <div className="bg-muted p-4 rounded-lg space-y-2 text-sm">
                <p className="font-medium">Windows:</p>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Download the PowerShell script using the button above</li>
                  <li>Run PowerShell as Administrator</li>
                  <li>Execute the command below</li>
                  <li>The agent will automatically start collecting metrics</li>
                </ol>
                <div className="mt-3 flex gap-2">
                  <Input readOnly value={windowsInstallCommand} className="font-mono text-xs" />
                  <Button variant="outline" size="icon" onClick={() => copyToClipboard(windowsInstallCommand)}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowTokenDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
